import React, { useMemo, useState } from 'react';
import { Formik, Form, FieldArray } from 'formik';
import { post } from 'axios';
import PropTypes from 'prop-types';

import { actionSvgs } from 'assets/icons';

import FilledButton from 'components/Buttons/FilledButton';
import ModalContiner from 'components/ModalContainer';

import { renderToast } from 'components/Informations/Toast/utils';

import endpoints from 'services/endpoints';

import * as Yup from 'yup';
import * as S from '../styles';

const Add = () => actionSvgs('add');
const Delete = () => actionSvgs('remove');

const { insertBlock, updateBlock } = endpoints;

export default function ModalBlocks({ data, open, setOpen, type }) {
  const {
    id_area,
    id_bloco,
    nome,
    descricao,
    id_funcionario: idFuncionario,
    observer,
  } = data;

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    autoClose: 6000,
  });

  const initialValues = useMemo(() => {
    if (type === 'add') {
      return {
        nome: '',
        descricao: '',
        questoes: [
          {
            questao: '',
            peso: 0,
            dificuldade: 0,
          },
        ],
      };
    }
    return {
      nome,
      descricao,
    };
  }, [type, nome, descricao]);

  const schema = useMemo(() => {
    if (type === 'add') {
      return Yup.object().shape({
        nome: Yup.string()
          .min(3, 'O nome deve conter no mínimo 3 caractéres')
          .required('Campo obrigatório'),
        descricao: Yup.string()
          .max(255, 'Limite de caractéres excedido')
          .nullable(),
        questoes: Yup.array().of(
          Yup.object().shape({
            questao: Yup.string()
              .min(3, 'O nome deve conter no mínimo 3 caractéres')
              .max(255, 'Limite de caractéres excedido')
              .required('Campo obrigatório'),
            peso: Yup.number().required('Campo obrigatório'),
            dificuldade: Yup.number().required('Campo obrigatório'),
          })
        ),
      });
    }

    return Yup.object().shape({
      nome: Yup.string()
        .min(3, 'O nome deve conter no mínimo 3 caractéres')
        .required('Campo obrigatório'),
      descricao: Yup.string()
        .max(255, 'Limite de caractéres excedido')
        .nullable(),
    });
  }, [type]);

  const ModalTitle = useMemo(
    () => (type === 'add' ? 'Adicionar novo bloco' : 'Editar bloco'),
    [type]
  );

  const handleSubmit = (values, { resetForm }) => {
    if (type === 'add') {
      const allQuestions = values?.questoes;

      const objectToSend = {
        id_area,
        id_usr_in: idFuncionario,
        nome: values.nome,
        descricao: values.descricao,
        questoes: allQuestions.map(({ questao }) => questao),
      };

      return post(insertBlock, objectToSend)
        .then(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'success',
            message: 'Bloco inserido com sucesso',
            autoClose: 6000,
          });
        })
        .then(() => {
          setTimeout(() => {
            resetForm();
            observer();
            setOpen(false);
          }, 4000);
        })
        .catch(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'error',
            message: 'Erro ao inserir bloco',
            autoClose: 6000,
          });
        });
    }

    const objectToSend = {
      id_bloco,
      id_usr_up: idFuncionario,
      id_area,
      nome: values.nome,
      descricao: values.descricao,
    };

    return post(updateBlock, objectToSend)
      .then(() => {
        setToastVisibility(true);
        setToastInfos({
          type: 'success',
          message: 'Bloco atualizado com sucesso',
          autoClose: 6000,
        });
      })
      .then(() => {
        setTimeout(() => {
          resetForm();
          observer();
          setOpen(false);
        }, 4000);
      })
      .catch(() => {
        setToastVisibility(true);
        setToastInfos({
          type: 'error',
          message: 'Erro ao atualizar bloco',
          autoClose: 6000,
        });
      });
  };

  return (
    <>
      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.autoClose,
          setToastVisibility
        )}

      <ModalContiner isOpen={open} setIsOpen={setOpen} headText={ModalTitle}>
        <Formik
          initialValues={initialValues}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, setFieldValue, resetForm }) => (
            <Form>
              <S.FormField
                label="Nome do bloco"
                label_for="nome_bloco"
                error={!!errors?.nome}
                error_message={errors?.nome}
              >
                <S.FormInput
                  value={values.nome}
                  name="nome"
                  id="nome_bloco"
                  onChange={e => setFieldValue('nome', e.target.value)}
                  error={!!errors?.nome}
                />
              </S.FormField>

              <S.FormField
                label="Descrição"
                label_for="desc_bloco"
                error={!!errors?.descricao}
                error_message={errors?.descricao}
              >
                <S.TextField
                  value={values.descricao}
                  name="descricao"
                  id="desc_bloco"
                  onChange={e => setFieldValue('descricao', e.target.value)}
                  error={!!errors?.descricao}
                />
              </S.FormField>

              {type === 'add' && (
                <FieldArray
                  name="questoes"
                  render={arrayHelpers =>
                    values.questoes?.map((q, i) => (
                      <S.FormRow
                        error={
                          !!errors.questoes?.length &&
                          !!errors.questoes[i]?.questao
                        }
                      >
                        <S.FormField
                          label={i === 0 && 'Questões'}
                          label_for={`questao_${i}_bloco`}
                          error={
                            !!errors.questoes?.length &&
                            !!errors.questoes[i]?.questao
                          }
                          error_message={
                            !!errors.questoes?.length &&
                            errors.questoes[i]?.questao
                          }
                        >
                          <S.FormInput
                            value={q?.questao}
                            name={`questoes[${i}]`}
                            id={`questao_${i}_bloco`}
                            onChange={e =>
                              setFieldValue(
                                `questoes[${i}].questao`,
                                e.target.value
                              )
                            }
                            error={
                              !!errors.questoes?.length &&
                              errors.questoes[i]?.questao
                            }
                            error_message={
                              !!errors.questoes?.length &&
                              errors.questoes[i]?.questao
                            }
                          />
                        </S.FormField>

                        {values.questoes.length - 1 > 0 && (
                          <S.FormButton
                            className="icon"
                            kind={
                              values.questoes.length - 1 === 0
                                ? 'disbled'
                                : 'cancel'
                            }
                            icon={Delete}
                            disabled={values.questoes.length - 1 === 0}
                            onClick={e => {
                              e.preventDefault();
                              arrayHelpers.remove(i);
                            }}
                          />
                        )}

                        {values.questoes.length - 1 === i && (
                          <S.FormButton
                            className="icon"
                            kind="primary"
                            icon={Add}
                            onClick={e => {
                              e.preventDefault();
                              arrayHelpers.insert(
                                i + 1,
                                initialValues.questoes[0]
                              );
                            }}
                          />
                        )}
                      </S.FormRow>
                    ))
                  }
                />
              )}

              <S.ButtonsWrap>
                <FilledButton
                  text="Cancelar"
                  buttonClass="cancel"
                  handleClick={e => {
                    e.preventDefault();
                    resetForm();
                    setOpen(false);
                  }}
                />
                <FilledButton
                  text="enviar"
                  buttonType="submit"
                  buttonClass="confirm"
                />
              </S.ButtonsWrap>
            </Form>
          )}
        </Formik>
      </ModalContiner>
    </>
  );
}

ModalBlocks.defaultProps = {
  type: 'add',
  data: {
    nome: '',
    descricao: '',
  },
};

ModalBlocks.propTypes = {
  data: PropTypes.shape({
    id_area: PropTypes.number.isRequired,
    nome: PropTypes.string,
    descricao: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  type: PropTypes.string,
};
